import * as yup from "yup";
import i18n from "../i18n/i18n";

// Function to get translated validation messages
const t = (key: string): string => {
  try {
    return i18n.t(key);
  } catch (error) {
    console.error(`Translation error for key: ${key}`, error);
    return key; // Fallback to key if translation fails
  }
};

// Helper function for validating subtitle fields that depend on each other
const validateSubtitleField = (
  value: string | null | undefined,
  otherFieldValue: string | null | undefined,
  fieldName: string,
  otherFieldName: string,
  context: yup.TestContext<any>
) => {
  // Both can be empty
  if (
    (!value || value.trim() === "") &&
    (!otherFieldValue || otherFieldValue.trim() === "")
  ) {
    return true;
  }

  // If the other field is filled, this field must be filled too
  if (otherFieldValue && otherFieldValue.trim() !== "") {
    if (!value || value.trim() === "") {
      return context.createError({
        message: t("errors.field_required_when_other_filled")
          .replace("{fieldName}", fieldName)
          .replace("{otherFieldName}", otherFieldName),
      });
    }
  }

  // If value is filled, check length
  if (value && value.trim() !== "" && value.length < 2) {
    return context.createError({
      message: t("errors.field_min_length").replace("{fieldName}", fieldName),
    });
  }

  return true;
};

export const eventValidationSchema = yup.object({
  title: yup
    .string()
    .required(t("errors.title_en_required"))
    .min(2, t("errors.title_too_short")),
  title_pt: yup
    .string()
    .required(t("errors.title_pt_required"))
    .min(2, t("errors.title_pt_too_short")),
  subTitle: yup
    .string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .test({
      name: "conditional-required-subtitle",
      test: function (value, context) {
        const { subTitle_pt } = context.parent;
        return validateSubtitleField(
          value,
          subTitle_pt,
          t("events.subtitle") + " (EN)",
          t("events.subtitle") + " (PT)",
          context
        );
      },
    }),
  subTitle_pt: yup
    .string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .test({
      name: "conditional-required-subtitle-pt",
      test: function (value, context) {
        const { subTitle } = context.parent;
        return validateSubtitleField(
          value,
          subTitle,
          t("events.subtitle") + " (PT)",
          t("events.subtitle") + " (EN)",
          context
        );
      },
    }),
  description: yup
    .string()
    .required(t("errors.description_en_required"))
    .min(10, t("errors.description_min_length")),
  description_pt: yup
    .string()
    .required(t("errors.description_pt_required"))
    .min(10, t("errors.description_pt_min_length")),
  price: yup
    .string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .test({
      name: "price-validation",
      test: function (value, context) {
        // Determine if the event is free based on a hidden field in the form
        const { isFreeEvent } = context.parent;

        if (isFreeEvent === true) {
          return true; // Skip validation for free events
        }

        // For paid events, price is required and must be at least 2 characters
        if (!value || value.trim() === "") {
          return context.createError({
            message: t("errors.price_required"),
          });
        }

        if (value.length < 2) {
          return context.createError({
            message: t("errors.price_min_length"),
          });
        }

        return true;
      },
    }),
  price_pt: yup
    .string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .test({
      name: "price-pt-validation",
      test: function (value, context) {
        // Determine if the event is free based on a hidden field in the form
        const { isFreeEvent } = context.parent;

        if (isFreeEvent === true) {
          return true; // Skip validation for free events
        }

        // For paid events, price_pt is required and must be at least 2 characters
        if (!value || value.trim() === "") {
          return context.createError({
            message: t("errors.price_pt_required"),
          });
        }

        if (value.length < 2) {
          return context.createError({
            message: t("errors.price_pt_min_length"),
          });
        }

        return true;
      },
    }),
});
